import {getTurns} from '@github-ui/ahp-relay/ahp-session-selectors'
import type {ChatState} from '@microsoft/agent-host-protocol'
import {SlackIcon} from '@github-ui/custom-icons/SlackIcon'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {LinkExternalIcon} from '@primer/octicons-react'
import {Link, VisuallyHidden} from '@primer/react'
import type {APITask} from '../../../types/api-task'
import type {PublicUser} from '../../../types/public-user'
import {getChannelOrigin} from '../../../types/origin'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import type {Session} from '../../../types/session'
import {SessionMetadataRow} from '../../task-view/messages/SessionMetadataRow'
import microsoftTeamsIcon from './microsoft-teams.png'
import styles from './ChannelSessionAttribution.module.css'
import {extractTurnActor, extractTurnMessageType} from './turn-meta'

const copilotAgent: AgentInfo = {
  id: 'copilot',
  slug: 'copilot-swe-agent',
  display_name: 'Copilot',
  avatar_url: '',
  doc_url: '',
}

export function ChannelSessionAttribution({
  session,
  chatState,
  profiles,
  creatorType,
}: {
  session: Session
  chatState: ChatState | undefined
  profiles: ReadonlyMap<number, PublicUser>
  creatorType?: APITask['creator_type']
}) {
  const channelOrigin = getChannelOrigin(session.event_type)
  const eventUrl = session.event_url
  const firstUserTurn = chatState
    ? getTurns(chatState).find(turn => extractTurnMessageType(turn.message) === 'user')
    : undefined
  const actor = extractTurnActor(firstUserTurn?.message)
  const profile = actor ? profiles.get(actor.githubId) : undefined
  const login = creatorType === 'user' ? (session.user_login ?? profile?.login) : profile?.login

  if (!channelOrigin || !eventUrl || !login) return null

  if (isFeatureEnabled('copilot_mission_control_session_metadata')) {
    const attributedSession = profile ? {...session, user_id: profile.id, user_login: profile.login} : session
    return (
      <SessionMetadataRow
        currentUser={profile}
        session={attributedSession}
        environment="sandbox"
        agentInfo={copilotAgent}
      />
    )
  }

  return (
    <p className={styles.container}>
      <strong className={styles.login}>{login}</strong> <span className={styles.muted}>started this session via</span>{' '}
      <Link href={eventUrl} target="_blank" rel="noopener noreferrer" inline className={styles.sourceLink}>
        {channelOrigin === 'Slack' ? (
          <span data-testid="slack-channel-icon" className={styles.sourceIcon}>
            <SlackIcon />
          </span>
        ) : (
          <span data-testid="teams-channel-icon" className={styles.sourceIcon}>
            <img src={microsoftTeamsIcon} alt="" aria-hidden="true" className={styles.teamsImage} />
          </span>
        )}
        <span>{channelOrigin}</span>
        <LinkExternalIcon aria-hidden="true" />
        <VisuallyHidden> (opens in a new tab)</VisuallyHidden>
      </Link>{' '}
      <span className={styles.muted}>· Copilot</span>
    </p>
  )
}
